import axios from 'axios';

const API_BASE_URL = 'https://nrimarriage.in/api/v1';

export interface User {
  userId: string;
  firstName: string;
  email: string;
  profilePictureUrl?: string;
  lastName?: string;
  age?: number;
  gender?: string;
  location?: string;
}

export interface MatchedUser extends User {
  isMatch: boolean;
}

export interface MatchServiceResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

const getAuthHeaders = () => {
  const authToken = localStorage.getItem('authToken');
  if (!authToken) {
    throw new Error('No authentication token found');
  }
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${authToken}`
  };
};

const getCurrentUserId = (): string | null => {
  return localStorage.getItem('userId');
};

export const checkMutualLike = async (userId1: string, userId2: string): Promise<boolean> => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/like/${userId1}/${userId2}/exists`,
      { headers: getAuthHeaders() }
    );
    return response.data?.hasLiked || false;
  } catch (error) {
    console.error(`Error checking like between ${userId1} and ${userId2}:`, error);
    return false;
  }
};

export const getAllUsers = async (): Promise<MatchServiceResponse<User[]>> => {
  try {
    console.log('🔄 Fetching all users from API...');

    let allUsers: User[] = [];
    let currentPage = 1;
    let totalPages = 1;

    while (currentPage <= totalPages) {
      const response = await axios.get(
        `${API_BASE_URL}/users/GetUsers?pageNumber=${currentPage}&pageSize=100`,
        { headers: getAuthHeaders() }
      );

      if (response.data && response.data.results) {
        const validUsers = response.data.results.filter((user: any) => {
          if (!user.userId) {
            console.warn('⚠️ Skipping user without userId:', user);
            return false;
          }
          return true;
        });

        allUsers = [...allUsers, ...validUsers];
        totalPages = response.data.totalPages || 1;
        currentPage++;

        console.log(`📄 Fetched page ${currentPage - 1}/${totalPages} (${validUsers.length} users)`);
      } else {
        console.error('Unexpected response format:', response.data);
        break;
      }
    }

    console.log(`✅ Fetched ${allUsers.length} total users from API`);

    return {
      success: true,
      data: allUsers,
      message: 'Users fetched successfully'
    };
  } catch (error: any) {
    console.error('❌ Error fetching users:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch users'
    };
  }
};

export const getMatchedUsers = async (): Promise<MatchServiceResponse<MatchedUser[]>> => {
  try {
    const currentUserId = getCurrentUserId();

    if (!currentUserId) {
      return {
        success: false,
        message: 'User not authenticated'
      };
    }

    console.log('🔄 Fetching matched users...');
    console.log('👤 Current user ID:', currentUserId);

    const usersResponse = await getAllUsers();

    if (!usersResponse.success || !usersResponse.data) {
      return {
        success: false,
        message: usersResponse.message || 'Failed to fetch users'
      };
    }

    const allUsers = usersResponse.data.filter(user => user.userId !== currentUserId);
    console.log(`📋 Checking ${allUsers.length} users for matches...`);

    const matchedUsers: MatchedUser[] = [];
    let checkedCount = 0;

    for (const user of allUsers) {
      try {
        const theyLikedMe = await checkMutualLike(user.userId, currentUserId);

        if (theyLikedMe) {
          const iLikedThem = await checkMutualLike(currentUserId, user.userId);

          if (iLikedThem) {
            console.log(`💚 Match found with user: ${user.firstName} (${user.userId})`);
            matchedUsers.push({
              ...user,
              isMatch: true
            });
          }
        }

        checkedCount++;

        if (checkedCount % 10 === 0) {
          console.log(`⏳ Checked ${checkedCount}/${allUsers.length} users...`);
        }
      } catch (error) {
        console.error(`Error checking match with user ${user.userId}:`, error);
      }
    }

    console.log(`✅ Found ${matchedUsers.length} matched users out of ${allUsers.length} total users`);

    const cachedMatches = {
      matches: matchedUsers,
      timestamp: Date.now(),
      userId: currentUserId
    };
    localStorage.setItem(`cachedMatches_${currentUserId}`, JSON.stringify(cachedMatches));

    return {
      success: true,
      data: matchedUsers,
      message: `Found ${matchedUsers.length} matches`
    };
  } catch (error: any) {
    console.error('❌ Error getting matched users:', error);
    return {
      success: false,
      message: error.message || 'Failed to get matched users'
    };
  }
};

export const getCachedMatches = (): MatchedUser[] | null => {
  try {
    const currentUserId = getCurrentUserId();
    if (!currentUserId) return null;

    const cached = localStorage.getItem(`cachedMatches_${currentUserId}`);
    if (!cached) return null;

    const { matches, timestamp, userId } = JSON.parse(cached);

    if (userId !== currentUserId) {
      console.log('🚫 Cache userId mismatch, clearing cache');
      localStorage.removeItem(`cachedMatches_${currentUserId}`);
      return null;
    }

    const CACHE_DURATION = 5 * 60 * 1000;

    if (Date.now() - timestamp > CACHE_DURATION) {
      localStorage.removeItem(`cachedMatches_${currentUserId}`);
      return null;
    }

    return matches;
  } catch (error) {
    console.error('Error reading cached matches:', error);
    return null;
  }
};

export const clearMatchCache = () => {
  const currentUserId = getCurrentUserId();
  if (currentUserId) {
    localStorage.removeItem(`cachedMatches_${currentUserId}`);
  }

  const allKeys = Object.keys(localStorage);
  allKeys.forEach(key => {
    if (key.startsWith('cachedMatches_')) {
      localStorage.removeItem(key);
    }
  });

  console.log('🗑️ Match cache cleared');
};

export const matchService = {
  getMatchedUsers,
  getAllUsers,
  checkMutualLike,
  getCachedMatches,
  clearMatchCache
};
